import { Box, ClickAwayListener, Stack, Typography } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  BARE_TOOLTIP_SLOT_SX,
  PILL_HOVER_CARD_ACTION_CLASS,
  pillHoverCardActionSx,
  pillHoverCardInfoRowSx,
  pillHoverCardInfoValueSx,
  pillHoverCardSx,
  pillHoverCardTitleTextSx,
} from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/pill-hover-card.styles';
import { useHoverCardDelay } from 'src/utils/hooks/use-hover-card-delay';

interface PillHoverCardProps {
  card: ReactNode;
  hoverCard: ReturnType<typeof useHoverCardDelay>;
  children: ReactElement;
}

export const PillHoverCard = ({ card, hoverCard, children }: PillHoverCardProps) => (
  <ClickAwayListener onClickAway={hoverCard.closeNow}>
    <Tooltip
      placement="bottom-start"
      open={hoverCard.isOpen}
      onClose={hoverCard.closeNow}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      nonInteractable={false}
      slotProps={{
        popper: {
          sx: { pointerEvents: 'auto' },
        },
        tooltip: {
          onMouseEnter: hoverCard.openNow,
          onMouseLeave: hoverCard.closeSoon,
          sx: BARE_TOOLTIP_SLOT_SX,
        },
      }}
      title={<Box sx={pillHoverCardSx}>{card}</Box>}
    >
      {children}
    </Tooltip>
  </ClickAwayListener>
);

interface PillHoverCardTitleProps {
  leading: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  /** Wraps a long title instead of truncating it. */
  wrap?: boolean;
}

export const PillHoverCardTitle = ({
  leading,
  children,
  trailing,
  wrap = false,
}: PillHoverCardTitleProps) => (
  <Stack direction="row" alignItems="center" gap="0.5rem" sx={{ mb: '0.4375rem' }}>
    {leading}
    <Typography component="span" noWrap={!wrap} sx={pillHoverCardTitleTextSx(wrap)}>
      {children}
    </Typography>
    {trailing}
  </Stack>
);

interface PillHoverCardInfoRowProps {
  icon: string;
  children: ReactNode;
  /** Revealed while the row is hovered. */
  action?: ReactNode;
}

export const PillHoverCardInfoRow = ({
  icon,
  children,
  action,
}: PillHoverCardInfoRowProps) => (
  <Stack direction="row" alignItems="center" gap="0.4375rem" sx={pillHoverCardInfoRowSx}>
    <DynamicIcon name={icon} library="lucide" size={13} sx={{ flexShrink: 0 }} />
    <Typography component="span" noWrap sx={pillHoverCardInfoValueSx}>
      {children}
    </Typography>
    {action ? (
      <Box
        component="span"
        className={PILL_HOVER_CARD_ACTION_CLASS}
        sx={pillHoverCardActionSx}
      >
        {action}
      </Box>
    ) : null}
  </Stack>
);
